import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import {
    Plus,
    Search,
    Trash2
} from 'lucide-react';
import { format } from 'date-fns';

interface VisaAppointment {
    id: string;
    status: string;
    visa_type: string;
    communication_status: string;
    preferred_country: string;
    full_name: string;
    appointment_date: string;
    birth_date: string;
    passport_number: string;
    phone: string;
    email: string;
    application_center: string;
    secondary_country: string;
    pnr: string;
    service_type: string;
    visit_purpose: string;
    description: string;
    created_at: string;
}

import { COUNTRIES, COUNTRY_CONFIG } from '../../utils/visaConfig';

const STATUS_OPTIONS = [
    'Tamamlandı',
    'Bekleniyor',
    'Kontrol',
    'İptal',
    'Ertelendi',
    'İptal ???'
];

const VisaAppointmentsPage: React.FC = () => {
    const { user } = useAuth();
    const [appointments, setAppointments] = useState<VisaAppointment[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    // Form State
    const [formData, setFormData] = useState({
        status: 'Bekleniyor',
        visa_type: 'Vissiz',
        communication_status: 'Aranmadı',
        preferred_country: '',
        full_name: '',
        appointment_date: '',
        birth_date: '',
        passport_number: '',
        phone: '',
        email: '',
        application_center: '',
        secondary_country: '',
        pnr: '',
        service_type: '',
        visit_purpose: '',
        description: ''
    });

    useEffect(() => {
        fetchAppointments();
    }, []);

    const fetchAppointments = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('visa_appointments')
                .select('*')
                .order('appointment_date', { ascending: true });

            if (error) throw error;
            setAppointments(data || []);
        } catch (error) {
            console.error('Randevular yüklenirken hata:', error);
        } finally {
            setLoading(false);
        }
    };

    const sendAppointmentSms = async (phone: string, fullName: string, dateStr: string) => {
        if (!phone) return;

        try {
            const date = new Date(dateStr);
            const formattedDate = format(date, "dd.MM.yyyy HH:mm");
            const message = `Sayın ${fullName}, vize randevunuz ${formattedDate} tarihine oluşturulmuştur. Lütfen randevu saatinden 15 dakika önce başvuru merkezinde olunuz.`;

            // Clean phone number
            const cleanedPhone = phone.replace(/\D/g, '');

            await supabase.functions.invoke('send-sms', {
                body: {
                    phones: [cleanedPhone],
                    message,
                    userId: user?.id,
                    branchId: (user as any)?.branchId
                }
            });
            console.log('Randevu SMS\'i gönderildi:', cleanedPhone);
        } catch (err) {
            console.error('SMS gönderme hatası:', err);
        }
    };

    const handleAddAppointment = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            // Construct payload explicitly to ensure nulls for empty strings
            const payload = {
                status: formData.status,
                visa_type: formData.visa_type,
                communication_status: formData.communication_status,
                preferred_country: formData.preferred_country || null,
                full_name: formData.full_name,
                appointment_date: formData.appointment_date ? new Date(formData.appointment_date).toISOString() : null,
                birth_date: formData.birth_date || null,
                passport_number: formData.passport_number || null,
                phone: formData.phone || null,
                email: formData.email || null,
                application_center: formData.application_center || null,
                secondary_country: formData.secondary_country || null,
                pnr: formData.pnr || null,
                service_type: formData.service_type || null,
                visit_purpose: formData.visit_purpose || null,
                description: formData.description || null,
                created_by: user?.id
            };

            const { error } = await supabase.from('visa_appointments').insert(payload);

            if (error) {
                console.error('Supabase hatası:', error);
                throw error;
            }

            setShowAddModal(false);
            setFormData({
                status: 'Bekleniyor',
                visa_type: 'Vissiz',
                communication_status: 'Aranmadı',
                preferred_country: '',
                full_name: '',
                appointment_date: '',
                birth_date: '',
                passport_number: '',
                phone: '',
                email: '',
                application_center: '',
                secondary_country: '',
                pnr: '',
                service_type: '',
                visit_purpose: '',
                description: ''
            });
            fetchAppointments();

            // Send SMS if appointment date is set
            if (payload.appointment_date && payload.phone) {
                await sendAppointmentSms(payload.phone, payload.full_name, payload.appointment_date);
            }

            alert('Randevu başarıyla oluşturuldu.');
        } catch (error: any) {
            console.error('Randevu eklenirken hata:', error);
            alert('Randevu eklenirken bir hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
        }
    };

    const handleDelete = async (id: string) => {
        if (!await window.appConfirm('Bu randevu kaydını silmek istediğinize emin misiniz?')) return;

        try {
            const { error } = await supabase
                .from('visa_appointments')
                .delete()
                .eq('id', id);

            if (error) throw error;
            fetchAppointments();
        } catch (error) {
            console.error('Silme işlemi başarısız:', error);
            alert('Silme işlemi sırasında hata oluştu.');
        }
    };

    // Update function for inline editing
    const handleUpdate = async (id: string, field: keyof VisaAppointment, value: any) => {
        try {
            // If value is empty string for optional fields, send null
            let processedValue = value;
            if (value === '' && field !== 'full_name' && field !== 'status') {
                processedValue = null;
            }

            // Date handling
            if (field === 'appointment_date' && value) {
                processedValue = new Date(value).toISOString();
            }

            const { error } = await supabase
                .from('visa_appointments')
                .update({ [field]: processedValue })
                .eq('id', id);

            if (error) throw error;

            // Optimistic update
            setAppointments(prev => prev.map(app =>
                app.id === id ? { ...app, [field]: processedValue } : app
            ));

            // Send SMS if date is updated
            if (field === 'appointment_date' && processedValue) {
                const app = appointments.find(a => a.id === id);
                if (app && app.phone) {
                    await sendAppointmentSms(app.phone, app.full_name, processedValue);
                }
            }

        } catch (error) {
            console.error('Güncelleme hatası:', error);
            // Revert on error (fetch fresh data)
            fetchAppointments();
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'Tamamlandı': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
            case 'Bekleniyor': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
            case 'İptal':
            case 'İptal ???': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
            case 'Kontrol': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
            default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
        }
    };

    const filteredAppointments = appointments.filter(app => {
        const matchesSearch = app.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            app.passport_number?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || app.status === filterStatus;

        return matchesSearch && matchesStatus;
    });

    return (
        <div className="p-6 max-w-full mx-auto space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Randevu Kayıtları</h1>
                    <p className="text-gray-500 dark:text-gray-400">Tüm vize randevularını buradan yönetebilirsiniz.</p>
                </div>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Plus className="w-5 h-5" />
                    <span>Yeni Kayıt</span>
                </button>
            </div>

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
                <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="İsim, pasaport no ara..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg leading-5 bg-white dark:bg-gray-800 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white"
                    />
                </div>
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="block w-full md:w-48 pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                    <option value="all">Tüm Durumlar</option>
                    {STATUS_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-max divide-y divide-gray-200 dark:divide-gray-800 text-xs text-left">
                        <thead className="bg-gray-50 dark:bg-gray-800/50">
                            <tr>
                                <th scope="col" className="px-3 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Durum</th>
                                <th scope="col" className="px-3 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Vize</th>
                                <th scope="col" className="px-3 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">İletişim</th>
                                <th scope="col" className="px-3 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ülke (Tercih)</th>
                                <th scope="col" className="px-3 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">İsim Soyisim</th>
                                <th scope="col" className="px-3 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Randevu Tarihi</th>
                                <th scope="col" className="px-3 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Doğum Tarihi</th>
                                <th scope="col" className="px-3 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Pass No</th>
                                <th scope="col" className="px-3 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Telefon</th>
                                <th scope="col" className="px-3 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">E-mail</th>
                                <th scope="col" className="px-3 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Başvuru Mrk.</th>
                                <th scope="col" className="px-3 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ülke (Başvuru)</th>
                                <th scope="col" className="px-3 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">PNR</th>
                                <th scope="col" className="px-3 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Hizmet Türü</th>
                                <th scope="col" className="px-3 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Gidiş Amacı</th>
                                <th scope="col" className="px-3 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Açıklama</th>
                                <th scope="col" className="relative px-3 py-3">
                                    <span className="sr-only">İşlemler</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                            {loading ? (
                                <tr><td colSpan={17} className="px-6 py-4 text-center text-sm text-gray-500">Yükleniyor...</td></tr>
                            ) : filteredAppointments.length === 0 ? (
                                <tr><td colSpan={17} className="px-6 py-4 text-center text-sm text-gray-500">Kayıt bulunamadı.</td></tr>
                            ) : (
                                filteredAppointments.map((app) => (
                                    <tr key={app.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group">
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <select
                                                value={app.status || 'Bekleniyor'}
                                                onChange={(e) => handleUpdate(app.id, 'status', e.target.value)}
                                                className={`text-xs rounded-full px-2 py-1 border-0  focus:ring-1 focus:ring-blue-500 cursor-pointer ${getStatusBadge(app.status)}`}
                                            >
                                                {STATUS_OPTIONS.map(opt => <option key={opt} value={opt} className="text-gray-900 bg-white">{opt}</option>)}
                                            </select>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <select
                                                value={app.visa_type || 'Vissiz'}
                                                onChange={(e) => handleUpdate(app.id, 'visa_type', e.target.value)}
                                                className="bg-transparent text-xs border-transparent hover:border-gray-200 focus:border-blue-500 focus:ring-0 rounded w-20 p-1"
                                            >
                                                <option value="Visli">Visli</option>
                                                <option value="Vissiz">Vissiz</option>
                                            </select>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <select
                                                value={app.communication_status || 'Aranmadı'}
                                                onChange={(e) => handleUpdate(app.id, 'communication_status', e.target.value)}
                                                className={`bg-transparent text-xs border-transparent hover:border-gray-200 focus:border-blue-500 focus:ring-0 rounded w-24 p-1 ${app.communication_status === 'Arandı' ? 'text-green-600 font-medium' : 'text-gray-500'}`}
                                            >
                                                <option value="Arandı">Arandı</option>
                                                <option value="Aranmadı">Aranmadı</option>
                                            </select>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <select
                                                value={app.preferred_country || ''}
                                                onChange={(e) => handleUpdate(app.id, 'preferred_country', e.target.value)}
                                                className="bg-transparent text-xs border-transparent hover:border-gray-200 focus:border-blue-500 focus:ring-0 rounded w-24 p-1"
                                            >
                                                <option value="">Seçiniz</option>
                                                {COUNTRIES.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                                            </select>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap font-medium text-gray-900 dark:text-white">
                                            <input
                                                type="text"
                                                defaultValue={app.full_name}
                                                onBlur={(e) => handleUpdate(app.id, 'full_name', e.target.value)}
                                                className="bg-transparent border-transparent hover:border-gray-200 focus:border-blue-500 focus:ring-0 rounded w-40 p-1 text-xs font-medium"
                                            />
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-gray-500 dark:text-gray-400">
                                            <input
                                                type="datetime-local"
                                                value={app.appointment_date ? format(new Date(app.appointment_date), "yyyy-MM-dd'T'HH:mm") : ''}
                                                onChange={(e) => handleUpdate(app.id, 'appointment_date', e.target.value)}
                                                className="bg-transparent border-transparent hover:border-gray-200 focus:border-blue-500 focus:ring-0 rounded w-36 p-1 text-xs"
                                            />
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-gray-500 dark:text-gray-400">
                                            <input
                                                type="date"
                                                value={app.birth_date || ''}
                                                onChange={(e) => handleUpdate(app.id, 'birth_date', e.target.value)}
                                                className="bg-transparent border-transparent hover:border-gray-200 focus:border-blue-500 focus:ring-0 rounded w-28 p-1 text-xs"
                                            />
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-gray-500 dark:text-gray-400">
                                            <input
                                                type="text"
                                                defaultValue={app.passport_number || ''}
                                                onBlur={(e) => handleUpdate(app.id, 'passport_number', e.target.value)}
                                                className="bg-transparent border-transparent hover:border-gray-200 focus:border-blue-500 focus:ring-0 rounded w-24 p-1 text-xs"
                                                placeholder="-"
                                            />
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-gray-500 dark:text-gray-400">
                                            <input
                                                type="text"
                                                defaultValue={app.phone || ''}
                                                onBlur={(e) => handleUpdate(app.id, 'phone', e.target.value)}
                                                className="bg-transparent border-transparent hover:border-gray-200 focus:border-blue-500 focus:ring-0 rounded w-28 p-1 text-xs"
                                                placeholder="-"
                                            />
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-gray-500 dark:text-gray-400">
                                            <input
                                                type="text"
                                                defaultValue={app.email || ''}
                                                onBlur={(e) => handleUpdate(app.id, 'email', e.target.value)}
                                                className="bg-transparent border-transparent hover:border-gray-200 focus:border-blue-500 focus:ring-0 rounded w-40 p-1 text-xs"
                                                placeholder="-"
                                            />
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-gray-500 dark:text-gray-400">
                                            <input
                                                type="text"
                                                defaultValue={app.application_center || ''}
                                                onBlur={(e) => handleUpdate(app.id, 'application_center', e.target.value)}
                                                className="bg-transparent border-transparent hover:border-gray-200 focus:border-blue-500 focus:ring-0 rounded w-24 p-1 text-xs"
                                                placeholder="-"
                                            />
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <select
                                                value={app.secondary_country || ''}
                                                onChange={(e) => handleUpdate(app.id, 'secondary_country', e.target.value)}
                                                className="bg-transparent text-xs border-transparent hover:border-gray-200 focus:border-blue-500 focus:ring-0 rounded w-24 p-1"
                                            >
                                                <option value="">Seçiniz</option>
                                                {COUNTRIES.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                                            </select>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-gray-500 dark:text-gray-400">
                                            <input
                                                type="text"
                                                defaultValue={app.pnr || ''}
                                                onBlur={(e) => handleUpdate(app.id, 'pnr', e.target.value)}
                                                className="bg-transparent border-transparent hover:border-gray-200 focus:border-blue-500 focus:ring-0 rounded w-24 p-1 text-xs"
                                                placeholder="-"
                                            />
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-gray-500 dark:text-gray-400">
                                            <input
                                                type="text"
                                                defaultValue={app.service_type || ''}
                                                onBlur={(e) => handleUpdate(app.id, 'service_type', e.target.value)}
                                                className="bg-transparent border-transparent hover:border-gray-200 focus:border-blue-500 focus:ring-0 rounded w-24 p-1 text-xs"
                                                placeholder="-"
                                            />
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-gray-500 dark:text-gray-400">
                                            <input
                                                type="text"
                                                defaultValue={app.visit_purpose || ''}
                                                onBlur={(e) => handleUpdate(app.id, 'visit_purpose', e.target.value)}
                                                className="bg-transparent border-transparent hover:border-gray-200 focus:border-blue-500 focus:ring-0 rounded w-24 p-1 text-xs"
                                                placeholder="-"
                                            />
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-gray-500 dark:text-gray-400">
                                            <input
                                                type="text"
                                                defaultValue={app.description || ''}
                                                onBlur={(e) => handleUpdate(app.id, 'description', e.target.value)}
                                                className="bg-transparent border-transparent hover:border-gray-200 focus:border-blue-500 focus:ring-0 rounded w-32 p-1 text-xs truncate"
                                                placeholder="Not..."
                                                title={app.description || ''}
                                            />
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-right font-medium">
                                            <button onClick={() => handleDelete(app.id)} className="text-gray-400 hover:text-red-600 p-1 transition-colors opacity-0 group-hover:opacity-100">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add Modal */}
            {showAddModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm overflow-y-auto">
                    <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-4xl shadow-2xl my-8">
                        <div className="p-6 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-900 rounded-t-2xl z-10">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Yeni Randevu Kaydı</h2>
                            <button
                                onClick={() => setShowAddModal(false)}
                                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                            >
                                Kapat
                            </button>
                        </div>

                        <form onSubmit={handleAddAppointment} className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {/* Row 1 */}
                            <div>
                                <label className="block text-sm font-medium mb-1">Durum</label>
                                <select
                                    value={formData.status}
                                    onChange={e => setFormData({ ...formData, status: e.target.value })}
                                    className="w-full p-2 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
                                >
                                    {STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Vize</label>
                                <select
                                    value={formData.visa_type}
                                    onChange={e => setFormData({ ...formData, visa_type: e.target.value })}
                                    className="w-full p-2 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
                                >
                                    <option value="Visli">Visli</option>
                                    <option value="Vissiz">Vissiz</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">İletişim</label>
                                <select
                                    value={formData.communication_status}
                                    onChange={e => setFormData({ ...formData, communication_status: e.target.value })}
                                    className="w-full p-2 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
                                >
                                    <option value="Arandı">Arandı</option>
                                    <option value="Aranmadı">Aranmadı</option>
                                </select>
                            </div>

                            {/* Row 2 */}
                            <div>
                                <label className="block text-sm font-medium mb-1">Ülke (Tercih)</label>
                                <select
                                    value={formData.preferred_country}
                                    onChange={e => setFormData({ ...formData, preferred_country: e.target.value })}
                                    className="w-full p-2 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
                                >
                                    <option value="">Seçiniz</option>
                                    {COUNTRIES.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">İsim Soyisim</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.full_name}
                                    onChange={e => setFormData({ ...formData, full_name: e.target.value })}
                                    className="w-full p-2 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Randevu Tarihi</label>
                                <input
                                    type="datetime-local"
                                    value={formData.appointment_date}
                                    onChange={e => setFormData({ ...formData, appointment_date: e.target.value })}
                                    className="w-full p-2 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
                                />
                            </div>

                            {/* Row 3 */}
                            <div>
                                <label className="block text-sm font-medium mb-1">Doğum Tarihi</label>
                                <input
                                    type="date"
                                    value={formData.birth_date}
                                    onChange={e => setFormData({ ...formData, birth_date: e.target.value })}
                                    className="w-full p-2 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Pasaport No</label>
                                <input
                                    type="text"
                                    value={formData.passport_number}
                                    onChange={e => setFormData({ ...formData, passport_number: e.target.value })}
                                    className="w-full p-2 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Telefon</label>
                                <input
                                    type="text"
                                    value={formData.phone}
                                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                    className="w-full p-2 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
                                />
                            </div>

                            {/* Row 4 */}
                            <div>
                                <label className="block text-sm font-medium mb-1">E-mail</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full p-2 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Başvuru Merkezi</label>
                                <input
                                    type="text"
                                    value={formData.application_center}
                                    onChange={e => setFormData({ ...formData, application_center: e.target.value })}
                                    className="w-full p-2 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Ülke (Başvuru)</label>
                                <select
                                    value={formData.secondary_country}
                                    onChange={e => setFormData({ ...formData, secondary_country: e.target.value })}
                                    className="w-full p-2 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
                                >
                                    <option value="">Seçiniz</option>
                                    {COUNTRIES.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                                </select>
                            </div>

                            {/* Row 5 */}
                            <div>
                                <label className="block text-sm font-medium mb-1">PNR</label>
                                <input
                                    type="text"
                                    value={formData.pnr}
                                    onChange={e => setFormData({ ...formData, pnr: e.target.value })}
                                    className="w-full p-2 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Hizmet Türü</label>
                                <input
                                    type="text"
                                    value={formData.service_type}
                                    onChange={e => setFormData({ ...formData, service_type: e.target.value })}
                                    className="w-full p-2 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Gidiş Amacı</label>
                                <select
                                    value={formData.visit_purpose}
                                    onChange={e => setFormData({ ...formData, visit_purpose: e.target.value })}
                                    className="w-full p-2 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700 disabled:opacity-50"
                                    disabled={!formData.preferred_country && !formData.secondary_country}
                                >
                                    <option value="">Seçiniz</option>
                                    {(formData.preferred_country || formData.secondary_country) &&
                                        COUNTRY_CONFIG[formData.preferred_country || formData.secondary_country]?.visaTypes.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                </select>
                            </div>

                            {/* Full Width */}
                            <div className="md:col-span-2 lg:col-span-3">
                                <label className="block text-sm font-medium mb-1">Açıklama</label>
                                <textarea
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full p-2 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700 h-24"
                                />
                            </div>

                            <div className="md:col-span-2 lg:col-span-3 pt-4 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowAddModal(false)}
                                    className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    İptal
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    Kaydet
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VisaAppointmentsPage;
